import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { generateClassDisplayId, generateSessionDisplayId, generateUniqueCode } from '@/utils/dbUtils';
import { 
  Calendar, 
  Clock, 
  User, 
  BookOpen, 
  CreditCard, 
  ChevronRight,
  AlertCircle,
  Users,
  CheckCircle,
  Building,
  GraduationCap,
  CalendarDays,
  UserCheck,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Instructor } from '@/types';
import ClassWizardLayout from './ClassWizardLayout';
import { useClassWizard } from './ClassWizardContext';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface CustomerContact {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
  is_active?: boolean;
  job_title?: string | null;
}

interface SubscriptionWithRelations {
  id: string;
  state: string;
  subscription_customers: {
    id: string;
    customer_role: 'owner' | 'participant' | 'billing_contact';
    customer: {
      id: string;
      name: string;
    };
  }[];
  parent_subscription?: {
    id: string;
    display_id: string;
  };
}

const Step7Summary = () => {
  const navigate = useNavigate();
  const { state, resetWizard } = useClassWizard();
  const [instructors, setInstructors] = useState<Record<string, Instructor>>({});
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Validações dos passos anteriores
    if (!state.selectedCustomer) {
      toast.error('Selecione um cliente primeiro');
      navigate('/class-wizard/step1');
      return;
    }
    
    if (!state.selectedSubscription) {
      toast.error('Selecione uma assinatura primeiro');
      navigate('/class-wizard/step2');
      return;
    }
    
    if (!state.selectedCourseFamily) {
      toast.error('Selecione uma família de cursos primeiro');
      navigate('/class-wizard/step3');
      return;
    }
    
    if (!state.selectedCourse) {
      toast.error('Selecione um curso primeiro');
      navigate('/class-wizard/step4');
      return;
    }
    
    if (state.sessions.length === 0) {
      toast.error('Adicione pelo menos uma sessão primeiro');
      navigate('/class-wizard/step5');
      return;
    }
    
    if (state.selectedSponsors.length === 0) {
      toast.error('Selecione pelo menos um contato solicitante primeiro');
      navigate('/class-wizard/step6');
      return;
    }
    
    fetchInstructors();
    fetchContacts();
  }, [state, navigate]);

  const fetchInstructors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('instructors')
        .select('*');

      if (error) throw error;
      
      const instructorsMap: Record<string, Instructor> = {};
      data?.forEach(instructor => {
        instructorsMap[instructor.id] = instructor;
      });
      
      setInstructors(instructorsMap);
    } catch (error: any) {
      toast.error('Erro ao carregar instrutores', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      // Buscar contatos do cliente principal (owner da assinatura)
      const ownerCustomer = state.selectedSubscription?.subscription_customers.find(sc => sc.customer_role === 'owner');
      if (!ownerCustomer) {
        toast.error('Cliente proprietário da assinatura não encontrado');
        navigate('/class-wizard/step2');
        return;
      }

      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', ownerCustomer.customer.id)
        .eq('is_active', true);

      if (error) throw error;
      
      if (data) {
        setContacts(data);
      }
    } catch (error: any) {
      toast.error('Erro ao carregar contatos', {
        description: error.message
      });
    }
  };

  const handleCreateClass = async () => {
    if (!state.selectedSubscription || !state.selectedCourse || state.sessions.length === 0) {
      toast.error('Informações incompletas');
      return;
    }

    setSubmitting(true);
    
    try {
      // Gerar um display_id único para a classe no formato WZVFDE (6 caracteres alfanuméricos maiúsculos)
      const classDisplayId = await generateUniqueCode({
        tableName: 'classes',
        columnName: 'display_id',
        generateCodeFunction: generateClassDisplayId
      });
      
      // 1. Criar a classe
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .insert({
          course_id: state.selectedCourse.id,
          subscription_id: state.selectedSubscription.id,
          status: 'scheduled',
          instructor_id: state.sessions[0]?.instructor_id || null,
          display_id: classDisplayId
        })
        .select()
        .single();

      if (classError) throw classError;
      
      // 2. Criar as sessões com display_id no formato WZVFDE-43
      const sessionsPromises = state.sessions.map(async (session) => {
        // Gerar um display_id único para cada sessão baseado no display_id da classe
        const sessionDisplayId = await generateUniqueCode({
          tableName: 'class_sessions',
          columnName: 'display_id',
          generateCodeFunction: () => generateSessionDisplayId(classDisplayId)
        });
        
        return {
          class_id: classData.id,
          course_id: state.selectedCourse?.id,
          start_date: session.start_date,
          end_date: session.end_date,
          duration_minutes: session.duration_minutes,
          mode: session.mode,
          status: 'confirmed',
          instructor_id: session.instructor_id,
          display_id: sessionDisplayId
        };
      });
      
      const sessionsToInsert = await Promise.all(sessionsPromises);
      
      const { error: sessionsError } = await supabase
        .from('class_sessions')
        .insert(sessionsToInsert);

      if (sessionsError) throw sessionsError;
      
      // 3. Inserir os sponsors selecionados
      if (state.selectedSponsors.length > 0) {
        // Obter o usuário logado
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Usuário não autenticado');
        }

        const sponsorsToInsert = state.selectedSponsors.map(sponsorId => ({
          class_id: classData.id,
          customer_contact_id: sponsorId,
          status: 'active',
          created_by: user.id,
          updated_by: user.id
        }));
        
        const { error: sponsorsError } = await supabase
          .from('class_sponsors')
          .insert(sponsorsToInsert);
          
        if (sponsorsError) throw sponsorsError;
      }
      
      toast.success('Classe criada com sucesso!');
      // Navegar para a confirmação dentro do wizard
      navigate('/class-wizard/confirmation', { 
        state: { classId: classData.id }
      });
    } catch (error: any) {
      toast.error('Erro ao criar classe', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 
      ? `${hours}h${mins > 0 ? ` ${mins}min` : ''}`
      : `${mins}min`;
  };

  const getInstructorName = (instructorId: string) => {
    const instructor = instructors[instructorId];
    return instructor ? `${instructor.first_name} ${instructor.last_name}` : 'Carregando...';
  };

  const getModeBadge = (mode: string | string[]) => {
    if (Array.isArray(mode)) {
      return (
        <div className="flex flex-wrap gap-2">
          {mode.map((m, index) => getModeBadge(m))}
        </div>
      );
    }
    
    switch (mode) {
      case 'onlineSync':
        return <Badge key="sync" variant="outline" className="bg-[#ff4a8f] hover:bg-[#ff4a8f]/80 text-white border-0">Online Síncrono</Badge>;
      case 'in-person':
        return <Badge key="in-person" variant="outline" className="bg-[#c8e600] hover:bg-[#c8e600]/80 text-white border-0">Presencial</Badge>;
      case 'onlineAsync':
        return <Badge key="async" variant="outline" className="bg-[#3b65f5] hover:bg-[#3b65f5]/80 text-white border-0">Online Assíncrono</Badge>;
      default:
        return <Badge key="unknown" variant="outline">Desconhecido</Badge>;
    }
  };

  // Tratar o selectedSubscription como SubscriptionWithRelations
  const subscription = state.selectedSubscription as unknown as SubscriptionWithRelations;

  return (
    <ClassWizardLayout
      currentStep={7}
      title="Revisar e Confirmar"
      description="Revise todos os detalhes da classe antes de finalizar"
      showBackButton={false}
    >
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Customer Step */}
        <Card>
          <CardHeader className="pb-2 pt-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Building className="h-5 w-5 text-primary" />
                Cliente
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-primary hover:text-primary hover:bg-primary/10"
                onClick={() => navigate('/class-wizard/step1')}
              >
                Alterar
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-extrabold mb-1">Nome</div>
                <div className="text-xs text-muted-foreground">{state.selectedCustomer?.name}</div>
              </div>
              <div>
                <div className="text-xs font-extrabold mb-1">CNPJ</div>
                <div className="text-xs text-muted-foreground">{state.selectedCustomer?.business_identification_number}</div>
              </div>
                </div>
          </CardContent>
        </Card>

        {/* Subscription Step */}
        <Card>
          <CardHeader className="pb-2 pt-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-5 w-5 text-primary" />
                Assinatura
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-primary hover:text-primary hover:bg-primary/10"
                onClick={() => navigate('/class-wizard/step2')}
              >
                Alterar
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-extrabold mb-1">ID da Assinatura</div>
                                  <div className="text-xs text-muted-foreground">
                    {state.selectedSubscription?.id}
                  </div>
                </div>
              <div>
                <div className="text-xs font-extrabold mb-1">Status</div>
                <div>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Ativa
                  </Badge>
                </div>
                </div>
              </div>
            </CardContent>
        </Card>

        {/* Course Step */}
        <Card>
          <CardHeader className="pb-2 pt-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <GraduationCap className="h-5 w-5 text-primary" />
                Curso
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-primary hover:text-primary hover:bg-primary/10"
                onClick={() => navigate('/class-wizard/step4')}
              >
                Alterar
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
                </div>
            </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-extrabold mb-1">Nome do Curso</div>
                  <div className="text-xs text-muted-foreground">{state.selectedCourse?.name}</div>
                </div>
                <div>
                  <div className="text-xs font-extrabold mb-1">Família</div>
                  <div className="text-xs text-muted-foreground">{state.selectedCourseFamily?.name}</div>
                </div>
              </div>
              <div>
                <div className="flex flex-wrap gap-2">
                    {getModeBadge(state.selectedCourse?.mode || '')}
                </div>
                </div>
              </div>
            </CardContent>
        </Card>

        {/* Sessions Step */}
        <Card>
          <CardHeader className="pb-2 pt-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarDays className="h-5 w-5 text-primary" />
                Sessões
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-primary hover:text-primary hover:bg-primary/10"
                onClick={() => navigate('/class-wizard/step5')}
              >
                Alterar
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
              </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {state.sessions.map((session, index) => (
                <div key={index} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                  <div className="shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-primary" />
                      </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-xs mb-1">Sessão {index + 1}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(session.start_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(session.start_date), 'HH:mm')} - {format(new Date(session.end_date), 'HH:mm')}
                          </div>
                        </div>
                  <div className="shrink-0">
                    {getModeBadge(session.mode)}
                      </div>
                    </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sponsors Step */}
        <Card>
          <CardHeader className="pb-2 pt-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <UserCheck className="h-5 w-5 text-primary" />
                Solicitantes
              </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
                className="text-primary hover:text-primary hover:bg-primary/10"
                onClick={() => navigate('/class-wizard/step3')}
            >
              Alterar
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
              </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {state.selectedSponsors.map((sponsorId) => {
                const contact = contacts.find(c => c.id === sponsorId);
                return contact ? (
                  <div key={sponsorId} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-extrabold text-xs">{contact.first_name} {contact.last_name}</div>
                      <div className="text-xs text-muted-foreground">{contact.email}</div>
              </div>
                </div>
              ) : (
                  <div key={sponsorId} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-extrabold text-xs">Carregando...</div>
                    </div>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Action Bar */}
        <div className="border-t py-4">
          <div className="flex justify-between items-center">
            <Button 
              variant="outline"
              onClick={() => navigate('/class-wizard/step6')}
              disabled={submitting}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <Button
              onClick={handleCreateClass}
              disabled={submitting}
            >
              {submitting ? (
                <>Criando turma...</>
              ) : (
                <>
                  Agendar Formação
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </ClassWizardLayout>
  );
};

export default Step7Summary; 